import { useState } from 'react'
import { KeyRound, Loader2, Plus } from 'lucide-react'
import {
  useGetUsersQuery,
  type EntitiesCreateUserSessionResponse,
  type EntitiesUserResponse
} from '@axilon-apps/domains-identity/state'
import { PROXY_API_V1_URL, useListIngestIntakesQuery } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../../components/ui/button'
import { API_KEY_PERMISSION, apiKeyHasPermission, apiKeyPermissions } from './api-key-permissions'
import { enabledIntakes, sortedApiKeys } from './api-key-view'
import { ApiKeysTable } from './ApiKeysTable'
import { CreateApiKeyDialog } from './CreateApiKeyDialog'
import { IssuedTokenDialog } from './IssuedTokenDialog'

// The users API pages at 100; API keys are listed in one page.
const API_KEYS_PAGE_SIZE = 100

export function ApiKeysSection() {
  const [createOpen, setCreateOpen] = useState(false)
  const [issued, setIssued] = useState<{
    apiKey: EntitiesUserResponse
    token: EntitiesCreateUserSessionResponse
  } | null>(null)
  // API keys are service-account users; `only` embeds each account's sessions.
  const listQuery = useGetUsersQuery({
    page: 1,
    perPage: API_KEYS_PAGE_SIZE,
    enabled: true,
    serviceAccounts: 'only'
  })
  const intakesQuery = useListIngestIntakesQuery()
  const apiKeys = sortedApiKeys(listQuery.data?.items ?? [])
  const intakes = enabledIntakes(intakesQuery.data?.intakes ?? [])

  return (
    <>
      <section className="min-w-0">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-2.5">
            <KeyRound className="mt-0.5 size-4 shrink-0 text-ink-3" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-ink">API keys</h2>
              <p className="mt-0.5 text-xs text-ink-3">
                Manage credentials for automated systems and integrations.
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            className="h-7 px-2.5 text-xs [&_svg]:size-3.5"
            onClick={() => setCreateOpen(true)}
          >
            <Plus /> New key…
          </Button>
        </div>

        {listQuery.isLoading ? (
          <div className="flex items-center gap-2 border-y border-border py-8 text-sm text-ink-3">
            <Loader2 className="size-4 animate-spin" />
            Loading API keys…
          </div>
        ) : listQuery.isError ? (
          <div
            role="alert"
            className="border-y border-st-down/20 bg-st-down-bg px-3 py-3 text-sm text-st-down"
          >
            API keys are unavailable. Try again.
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="border-y border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm font-medium text-ink">No API keys yet</p>
            <p className="mt-1 text-xs text-ink-3">
              Create one for an uploader, gateway, or integration.
            </p>
          </div>
        ) : (
          <div className="min-w-0 border-y border-border">
            <ApiKeysTable apiKeys={apiKeys} intakes={intakes} proxyApiV1Url={PROXY_API_V1_URL} />
          </div>
        )}

        {intakesQuery.isError ? (
          <p className="mt-3 text-xs text-st-warn">
            Intake choices are temporarily unavailable. Key management still works.
          </p>
        ) : null}
      </section>

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onIssued={(apiKey, token) => setIssued({ apiKey, token })}
      />
      <IssuedTokenDialog
        open={issued !== null}
        keyName={issued?.apiKey.name ?? ''}
        issuedToken={issued?.token ?? null}
        intakes={intakes}
        showIntakeCommand={
          issued
            ? apiKeyHasPermission(
                apiKeyPermissions(issued.apiKey.roleAssignments),
                API_KEY_PERMISSION.IngestPush
              )
            : false
        }
        proxyApiV1Url={PROXY_API_V1_URL}
        onClose={() => setIssued(null)}
      />
    </>
  )
}
